/**
 * /equipment/preuse-templates/new — start a pre-use template as a DRAFT, either
 * copied from a starter in the API's code catalog (`POST /from-starter`) or
 * blank (`POST /`). Either way it opens on its own page to be edited, and goes
 * live only when an admin publishes it.
 *
 * A template is for ONE active equipment type; the API refuses an inactive or
 * missing one, so only active types are offered. With no types at all the page
 * says so and points at Settings → Equipment Types.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { useActiveEquipmentTypes, usePreuseMutations, usePreuseStarters } from '../../hooks/usePreuseTemplates';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canWritePreuse, preuseRefusalSentence } from '../../lib/preuse';
import { canUploadSourceForm } from '../../lib/roles';
import { DH_INPUT, DhEmpty, DhField, DhPanel } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';
import { NoEquipmentTypesNotice, PREUSE_LIST } from './PreuseTemplatesScreen';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';

export default function PreuseTemplateNewScreen() {
  const navigate = useNavigate();
  const back = useBackToList(PREUSE_LIST);
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canWrite = canWritePreuse(user?.role, elevated);
  const types = useActiveEquipmentTypes();
  const starters = usePreuseStarters();
  const m = usePreuseMutations();

  const [starterKey, setStarterKey] = useState('');
  const [starterType, setStarterType] = useState('');
  const [starterName, setStarterName] = useState('');
  const [blankName, setBlankName] = useState('');
  const [blankType, setBlankType] = useState('');

  const typeList = types.data ?? [];
  const starterList = starters.data ?? [];
  const chosen = starterList.find((s) => s.key === starterKey) ?? null;

  // Picking a starter suggests the tenant's type of the same name, if it has one.
  useEffect(() => {
    if (!chosen) return;
    const match = typeList.find((t) => t.name.trim().toLowerCase() === chosen.suggestedEquipmentType.trim().toLowerCase());
    if (match) setStarterType(match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [starterKey]);

  const opened = (id: string | undefined) => {
    if (id) navigate(`${PREUSE_LIST}/${id}`, { replace: true, state: { listUrl: PREUSE_LIST } });
  };

  const typeFailure = queryLoadFailure(types);
  const noTypes = !types.isLoading && !typeFailure && typeList.length === 0;

  const typeSelect = (value: string, onChange: (v: string) => void, label: string) => (
    <DhField label={label}>
      <select className={DH_INPUT} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose a type…</option>
        {typeList.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    </DhField>
  );

  return (
    <div data-testid="preuse-template-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow="Pre-use templates" title="New template" onBack={back} backLabel="Back to templates" />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canWrite ? (
            <DhPanel eyebrow="New template" title="Not available">
              <DhEmpty>Only an admin or office staff can create a pre-use template.</DhEmpty>
            </DhPanel>
          ) : types.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading equipment types" />
            </div>
          ) : typeFailure ? (
            <DhPanel eyebrow="New template" title="Equipment types">
              <LoadFailureNotice error={queryFailureError(types)} kind={typeFailure} subject="equipment types" />
            </DhPanel>
          ) : noTypes ? (
            <DhPanel eyebrow="New template" title="Add an equipment type first">
              <NoEquipmentTypesNotice />
            </DhPanel>
          ) : (
            <>
              <DhPanel eyebrow="Start from a starter list" title="From a starter" testId="preuse-from-starter">
                <div className="space-y-4 p-4">
                  <p className="text-sm text-fieldiq-text">
                    A starting point written from common manufacturer and industry practice — not a regulatory form. It is copied into this company as a draft you can edit before an admin publishes it.
                  </p>
                  {starters.isLoading ? (
                    <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading starters" />
                  ) : queryLoadFailure(starters) ? (
                    <LoadFailureNotice error={queryFailureError(starters)} kind={queryLoadFailure(starters)!} subject="starter lists" />
                  ) : starterList.length === 0 ? (
                    <p className="text-sm text-fieldiq-text">There are no starter lists.</p>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" role="radiogroup" aria-label="Starter list">
                      {starterList.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          role="radio"
                          aria-checked={starterKey === s.key}
                          onClick={() => setStarterKey(s.key)}
                          className={`rounded-fi-panel border p-3 text-left transition ${
                            starterKey === s.key ? 'border-fi-amber bg-fi-amber/10' : 'border-fieldiq-border bg-fieldiq-dark/40 hover:border-fieldiq-muted'
                          }`}
                        >
                          <p className="text-sm font-extrabold text-white">{s.name}</p>
                          <p className="mt-1 text-xs text-fieldiq-muted">
                            {s.itemCount} items · {s.criticalCount} critical · for {s.suggestedEquipmentType}
                          </p>
                          <p className="mt-2 text-xs text-fieldiq-text">{s.description}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="grid gap-4 sm:grid-cols-2">
                    {typeSelect(starterType, setStarterType, 'Equipment type *')}
                    <DhField label="Name" hint={chosen ? `Leave blank to use “${chosen.name}”.` : undefined}>
                      <input className={DH_INPUT} value={starterName} maxLength={200} onChange={(e) => setStarterName(e.target.value)} />
                    </DhField>
                  </div>
                  <button
                    type="button"
                    className={PRIMARY}
                    disabled={!starterKey || !starterType || m.fromStarter.isPending}
                    onClick={async () => {
                      try {
                        const t = await m.fromStarter.mutateAsync({
                          starter_key: starterKey,
                          equipment_type_id: starterType,
                          ...(starterName.trim() ? { name: starterName.trim() } : {}),
                        });
                        toast.success('Draft created');
                        opened(t?.id);
                      } catch (error) {
                        toast.error(preuseRefusalSentence(error, 'The draft was not created.'));
                      }
                    }}
                  >
                    Create draft from starter
                  </button>
                </div>
              </DhPanel>

              <DhPanel eyebrow="Or write your own" title="Blank template" testId="preuse-blank">
                <div className="space-y-4 p-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <DhField label="Name *">
                      <input className={DH_INPUT} value={blankName} maxLength={200} onChange={(e) => setBlankName(e.target.value)} />
                    </DhField>
                    {typeSelect(blankType, setBlankType, 'Equipment type *')}
                  </div>
                  <button
                    type="button"
                    className={PRIMARY}
                    disabled={!blankName.trim() || !blankType || m.create.isPending}
                    onClick={async () => {
                      try {
                        const t = await m.create.mutateAsync({ name: blankName.trim(), equipment_type_id: blankType });
                        toast.success('Draft created');
                        opened(t?.id);
                      } catch (error) {
                        toast.error(preuseRefusalSentence(error, 'The draft was not created.'));
                      }
                    }}
                  >
                    Create blank draft
                  </button>
                </div>
              </DhPanel>

              {canUploadSourceForm(user?.role) && (
                <DhPanel eyebrow="Or read your paper form" title="From a document" testId="preuse-from-document-entry">
                  <div className="space-y-3 p-4">
                    <p className="text-sm text-fieldiq-text">
                      Upload the checklist your crews already use. The AI reads its items and guesses which are critical; you review every item, and
                      approving creates a draft.
                    </p>
                    <Link to={`${PREUSE_LIST}/from-document`} className={PRIMARY}>
                      Build from a document
                    </Link>
                  </div>
                </DhPanel>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
